import * as React from 'react';
import { ListItemButton, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';

/**
 * A styled input component that is visually hidden but remains functional.
 * This allows the file input to be triggered by clicking the styled button.
 */
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * A file input component that allows users to upload files via click or drag-and-drop.
 * It is styled as a large drop zone with an upload icon and dynamic text.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {function} props.handleSelect - A callback function triggered when a file is selected or dropped.
 * @returns {JSX.Element} The rendered file input component.
 * @example
 * <FileInput handleSelect={(event) => console.log(event.target.files)} />
 */
function FileInput({ handleSelect }) {
  /**
   * State to track if a file is being dragged over the component.
   * Used to trigger visual feedback (dashed border and background color).
   */
  const [dragActive, setDragActive] = React.useState(false);

  /**
   * Handles drag events (enter, over, leave) to update the visual state.
   * @param {DragEvent} e - The native drag event.
   */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  /**
   * Handles the drop event, extracts files, and mimics an input change event
   * to maintain compatibility with existing logic.
   * @param {DragEvent} e - The native drop event.
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // Create a fake event object to match the <input type="file"> structure
      const fakeEvent = {
        target: {
          files: e.dataTransfer.files
        }
      };
      handleSelect(fakeEvent);
    }
  };

  return (
    <ListItemButton
      component="label"
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      sx={{
        border: dragActive ? '2px dashed #1976d2' : '2px solid transparent',
        backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        padding: '20px',
        borderRadius: '8px'
      }}
    >
      <CloudUploadIcon sx={{ fontSize: 40, color: '#1976d2' }} />
      <Typography variant="button" mt={1} color='#1976d2'>
        {dragActive ? "Suelta el archivo aquí" : "Haz clic o arrastra un archivo"}
      </Typography>
      <VisuallyHiddenInput
        type="file"
        onChange={handleSelect}
      />
    </ListItemButton>
  );
}

export default FileInput;